'use client';

import {
    Button,
    ButtonGroup,
    Heading,
    Icon,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    RadioField,
} from '@petsmart-ui/sparky';
import React, { useEffect, useState } from 'react';

import { getNextApiRoute } from '@libs/common-next';
import { apiFetch, capitalize, HttpMethod, handleApiFetchResponse, isLocaleCA } from '@libs/core';

import { SnackbarUtils } from '../../utils/snackbarUtils';
import { ExperianModalItem } from './ExperianModalItem';
import {
    Confidence,
    type ExperianFormatAddressResponse,
    type ExperianModalCoreType,
    type FormProps,
    type ValidatedFormProps,
} from './types';
import {
    extractAddressValuesLayout,
    getAddressVerificationMessage,
    getExperianAddressLayout,
    handleAddressValidationErrorsLayout,
} from './utils';
import './ExperianModal.scss';

const componentClassName = 'about-you-modal';

const secureRandom = () => globalThis.crypto.getRandomValues(new Uint32Array(1))[0];

export function ExperianModalCore({
    experianResponse,
    saveSelectedAddress,
    loading,
    setIsLoading,
    onEditAddressClick,
    onVisibilityChange,
    onSaveError,
    currentFormValues,
    setCurrentFormValues,
}: Readonly<ExperianModalCoreType>) {
    const [isActive, setIsActive] = useState(false);
    const [selectedAddress, setSelectedAddress] = useState<string>('original');

    /**
     * `ValidatedFormValues` state used for handling multiple matches and interaction-required responses from Experian.
     *
     * This state stores the suggestions received from the `experianResponse` when:
     * - Multiple matches are found.
     * - The confidence level is `Interaction required`.
     *
     * The suggestions stored in this state are displayed to the user within a modal, allowing them to choose a preferred address.
     *
     * Additionally, `ValidatedFormProps` contains the `GAK` (Global Address Key) for each suggestion.
     * The `GAK` is used to make a call to the `formatSelectedAddress` method, which:
     * - Formats the selected address based on the user's choice from the suggestions.
     * - Returns the correctly formatted address according to the required layout, whether for U.S. or Canadian addresses.
     */
    const [validatedFormValues, setValidatedFormValues] = useState<ValidatedFormProps[] | undefined>();

    const [keyCounter, setKeyCounter] = useState(0);

    const formatSelectedAddressWithHook = async (
        gak: string,
        zipCode?: string
    ): Promise<FormProps['address'] | null> => {
        const formatAddressResponse = await apiFetch<ExperianFormatAddressResponse | null>(
            `${getNextApiRoute('address/format-address-layout')}`,
            {
                method: HttpMethod.POST,
                body: JSON.stringify({
                    globalAddressKeyInput: gak,
                    layouts: getExperianAddressLayout(zipCode),
                }),
                headers: {
                    'content-type': 'application/json',
                },
            }
        );

        const result = handleApiFetchResponse(formatAddressResponse);
        const formattedAddress = result?.result?.addresses_formatted?.[0]?.address;
        if (!formattedAddress) {
            return null;
        }

        return extractAddressValuesLayout(formattedAddress);
    };

    const notifySaveError = () => {
        if (onSaveError) {
            onSaveError();
        } else {
            SnackbarUtils.showPredefined('SERVER_ERROR');
        }
    };

    useEffect(() => {
        if (experianResponse) {
            setSelectedAddress('');
        }
        setKeyCounter(keyCounter + 1);
    }, [isActive, experianResponse]);

    useEffect(() => {
        onVisibilityChange?.(isActive);
    }, [isActive, onVisibilityChange]);

    useEffect(() => {
        const handleResponse = async () => {
            if (!experianResponse) {
                return;
            }

            // Extracting the confidence level from the response
            const { confidence } = experianResponse.result;

            // If the response has a "verified match" confidence level, directly set the form values
            // using the values received from the Experian validate address endpoint
            if (confidence === Confidence.verifiedMatch) {
                const newValidatedAddress = extractAddressValuesLayout(
                    experianResponse.result?.addresses_formatted[0]?.address
                );

                if (
                    experianResponse?.result?.addresses_formatted &&
                    isLocaleCA() &&
                    experianResponse?.result.confidence === 'Verified match'
                ) {
                    newValidatedAddress.city = newValidatedAddress.city
                        .trim()
                        .toLowerCase()
                        .split(' ')
                        .map(word => capitalize(word))
                        .join(' ');
                }

                setCurrentFormValues({
                    ...currentFormValues,
                    address: newValidatedAddress,
                });

                try {
                    await saveSelectedAddress({
                        ...currentFormValues,
                        address: newValidatedAddress,
                    });
                } catch {
                    notifySaveError();
                }
            } else if (experianResponse) {
                // If the response does not have a "verified match" confidence level, handle the address validation errors
                // This involves displaying the appropriate modal depending on the confidence level:
                // - Partial modal
                // - Interaction required modal
                // - Multiple matches modal
                const openModal = () => {
                    setIsActive(true);
                };
                await handleAddressValidationErrorsLayout(
                    currentFormValues,
                    experianResponse,
                    setCurrentFormValues,
                    setValidatedFormValues,
                    openModal
                );
            }
        };

        handleResponse();
    }, [experianResponse]);

    if (!experianResponse) {
        return null;
    }

    const { confidence } = experianResponse.result;

    const onClose = () => {
        setIsActive(false);
    };

    const handleEditAddressClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        setSelectedAddress('original');
        onClose();
        onEditAddressClick?.();
    };

    const handleModalClose = (e?: React.MouseEvent | React.KeyboardEvent) => {
        e?.stopPropagation();
        e?.preventDefault();
        onClose();
    };

    const handleChange = (value: string) => {
        setSelectedAddress(value);
    };

    const [validatedAddress] = validatedFormValues ?? [];

    // Handler for when the user clicks the "Save" button on the Experian Modal
    const handleSaveClick = async (useOriginal?: boolean) => {
        try {
            setIsLoading(true);

            /**
             * Key for the address, which can either be an Experian address or the original address.
             *
             * - `GAK` (Global Address Key): This key is used to make a call to Experian, which returns a properly
             * formatted address depending on the layout specified in the request body.
             * - `original`: Refers to the original address (if `useOriginal` state value is true).
             *
             * The `selectedAddressKey` determines which address is used based on the `useOriginal` flag.
             */
            const selectedAddressKey = useOriginal ? 'original' : selectedAddress;

            const newFormValues =
                selectedAddressKey === 'original'
                    ? currentFormValues
                    : validatedFormValues?.find(e => e.gak === selectedAddressKey);

            if (!newFormValues) {
                setIsLoading(false);
                return;
            }

            const addressToSave =
                'gak' in newFormValues &&
                typeof newFormValues.gak === 'string' &&
                confidence === Confidence.multipleMatches
                    ? await formatSelectedAddressWithHook(
                          newFormValues.gak,
                          newFormValues.address?.zipCode || currentFormValues.address?.zipCode
                      )
                    : newFormValues.address;

            if (!addressToSave) {
                throw new Error('Address format or save failed');
            }

            const updatedFormValues = {
                ...currentFormValues,
                address: { ...addressToSave },
            };

            await saveSelectedAddress(updatedFormValues);
            setCurrentFormValues(updatedFormValues);

            onClose();
        } catch {
            onClose();
            notifySaveError();
        } finally {
            setIsLoading(false);
        }
    };

    const renderFooter = () => {
        switch (confidence) {
            case Confidence.interactionRequired:
                return (
                    <ButtonGroup>
                        <Button
                            className={`${componentClassName}__footer__cta`}
                            onClick={async () => await handleSaveClick()}
                            text="Use selected address"
                            variant="primary"
                            loading={loading}
                            disabled={loading}
                        />
                    </ButtonGroup>
                );
            case Confidence.premisesPartial:
            case Confidence.streetPartial:
                return (
                    <ButtonGroup>
                        <React.Fragment key=".0">
                            <Button
                                className={`${componentClassName}__footer__cta`}
                                text="Use original address"
                                onClick={async () => {
                                    setSelectedAddress('original');
                                    await handleSaveClick(true);
                                }}
                                loading={loading}
                                disabled={loading}
                            />
                            <Button
                                className={`${componentClassName}__footer__cta`}
                                text="Edit address"
                                variant="primary"
                                onClick={handleEditAddressClick}
                                disabled={loading}
                            />
                        </React.Fragment>
                    </ButtonGroup>
                );
            case Confidence.multipleMatches:
                return (
                    <ButtonGroup>
                        <React.Fragment key=".0">
                            <Button
                                className={`${componentClassName}__footer__cta`}
                                text="Edit original address"
                                onClick={handleEditAddressClick}
                                disabled={loading}
                            />
                            <Button
                                className={`${componentClassName}__footer__cta`}
                                text="Use selected address"
                                variant="primary"
                                onClick={async () => {
                                    await handleSaveClick();
                                }}
                                loading={loading}
                                disabled={loading}
                            />
                        </React.Fragment>
                    </ButtonGroup>
                );
            case Confidence.noMatches:
                return (
                    <ButtonGroup>
                        <React.Fragment key=".0">
                            <Button
                                className={`${componentClassName}__footer__cta`}
                                text="Use original address"
                                onClick={async () => {
                                    setSelectedAddress('original');
                                    await handleSaveClick(true);
                                }}
                                loading={loading && selectedAddress === 'original'}
                                disabled={loading && selectedAddress === 'original'}
                            />
                            <Button
                                className={`${componentClassName}__footer__cta`}
                                text="Edit address"
                                variant="primary"
                                onClick={handleEditAddressClick}
                                disabled={loading}
                            />
                        </React.Fragment>
                    </ButtonGroup>
                );
            default:
                return null;
        }
    };

    const renderBody = () => {
        switch (confidence) {
            case Confidence.interactionRequired:
                return (
                    <RadioField defaultValue="" selectedValue={selectedAddress} onChange={handleChange}>
                        <div>
                            <p>
                                <strong> Original Address </strong>
                            </p>

                            <div className={`${componentClassName}__interaction`}>
                                <RadioField.Item key={secureRandom()} value="original" text="" />
                                <ExperianModalItem
                                    addressName={`${currentFormValues?.firstName} ${currentFormValues?.lastName}`}
                                    city={currentFormValues?.address?.city}
                                    phone={currentFormValues?.phone}
                                    postalCode={currentFormValues?.address?.zipCode}
                                    state={currentFormValues?.address?.state}
                                    street={`${currentFormValues?.address?.streetAddress}`}
                                    extendedAddress={`${currentFormValues?.address?.apt}`}
                                    validationDetail={experianResponse?.result?.validation_detail}
                                    experianResponse={experianResponse}
                                />
                            </div>
                        </div>
                        <div>
                            <p>
                                <strong> Verified Address </strong>
                            </p>
                            <div className={`${componentClassName}__interaction`}>
                                <RadioField.Item key={secureRandom()} value={validatedAddress?.gak} text="" />
                                <ExperianModalItem
                                    addressName={`${currentFormValues?.firstName} ${currentFormValues?.lastName}`}
                                    city={validatedAddress?.address?.city}
                                    phone={validatedAddress?.phone}
                                    postalCode={validatedAddress?.address?.zipCode}
                                    state={validatedAddress?.address?.state}
                                    street={`${validatedAddress?.address?.streetAddress}`}
                                    extendedAddress={`${validatedAddress?.address?.apt}`}
                                />
                            </div>{' '}
                        </div>
                    </RadioField>
                );
            case Confidence.premisesPartial:
            case Confidence.streetPartial:
                return (
                    <div className={`${componentClassName}__verified`}>
                        <ExperianModalItem
                            addressName={`${currentFormValues?.firstName} ${currentFormValues?.lastName}`}
                            city={currentFormValues?.address?.city}
                            postalCode={currentFormValues?.address?.zipCode}
                            state={currentFormValues?.address?.state}
                            street={`${currentFormValues?.address?.streetAddress}`}
                            extendedAddress={`${currentFormValues?.address?.apt}`}
                            validationDetail={experianResponse?.result?.validation_detail}
                            experianResponse={experianResponse}
                        />
                    </div>
                );
            case Confidence.noMatches:
                return (
                    <div className={`${componentClassName}__no-matches`}>
                        <ExperianModalItem
                            addressName={`${currentFormValues?.firstName} ${currentFormValues?.lastName}`}
                            city={currentFormValues?.address?.city}
                            postalCode={currentFormValues?.address?.zipCode}
                            state={currentFormValues?.address?.state}
                            street={`${currentFormValues?.address?.streetAddress}`}
                            extendedAddress={`${currentFormValues?.address?.apt}`}
                        />
                    </div>
                );
            case Confidence.multipleMatches:
                return (
                    <RadioField
                        defaultValue=""
                        selectedValue={selectedAddress}
                        onChange={handleChange}
                        key={keyCounter}
                    >
                        {validatedFormValues?.map(formValue => (
                            <div key={formValue.gak} className={`${componentClassName}__multiple`}>
                                <RadioField.Item value={formValue?.gak} text="" />
                                <ExperianModalItem
                                    addressName={`${currentFormValues?.firstName} ${currentFormValues?.lastName}`}
                                    city={formValue?.address?.city}
                                    phone={formValue?.phone}
                                    postalCode={formValue?.address?.zipCode}
                                    state={formValue?.address?.state}
                                    street={`${formValue?.address?.streetAddress}`}
                                    extendedAddress={`${formValue?.address?.apt}`}
                                />
                            </div>
                        ))}
                    </RadioField>
                );
            default:
                return null;
        }
    };

    return (
        <Modal dismissible isActive={isActive} className={componentClassName} onClose={handleModalClose}>
            <ModalHeader data-testid="modal-header" closeButtonText="close">
                <Heading tagName="h6" className={`${componentClassName}__heading`}>
                    Verify your address
                </Heading>
                <button
                    className="sparky-c-button sparky-c-modal__close-button sparky-c-button--bare sparky-c-button--circle"
                    aria-pressed="true"
                    type="button"
                    onClick={handleModalClose}
                    data-testid="modal-close-btn"
                >
                    <Icon name="x" />
                </button>
            </ModalHeader>

            <ModalBody data-testid="modal-body">
                <p className={`${componentClassName}__description`}>
                    {!!confidence && getAddressVerificationMessage(confidence)}
                </p>
                {renderBody()}
            </ModalBody>

            <ModalFooter data-testid="modal-footer" className={`${componentClassName}__footer`} alignEnd>
                {renderFooter()}
            </ModalFooter>
        </Modal>
    );
}
